/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment due date modal
 *
 * @file AssignmentDueDateModal.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';
import Icon from '../icons/Icon';
import AssignmentDueDateModalField from './AssignmentDueDateModalField';
import { createModalDueDateValidator } from './assignmentFormValidation';
import { getBrowserTimezone, wallClockToUTC } from '../../common/dateTimeUtils';
import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { AssignmentDueDateModalStylesRoot, classes } from './styles/assignmentDueDateModalStyles';

function convertDueDateToUtcIso(dateStr) {
  const utcDate = wallClockToUTC(
    dateStr,
    TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME,
    getBrowserTimezone()
  );

  return utcDate?.toISOString() || '';
}

const AssignmentDueDateModal = ({
  open,
  assignment,
  textInfo,
  onClose,
  onDone,
  minDate,
  maxDate,
  filterDate,
  validateDueDate,
  returnStorageDate,
  placeholderText
}) => {
  const title = textInfo?.dueDateModalTitle || 'Assignment due date';
  const doneLabel = textInfo?.dueDateModalDoneLabel || 'Done';

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: { dueDate: '' },
    mode: 'onChange'
  });

  const dueDateValue = watch('dueDate');

  const dueDateRules = useMemo(
    () => ({ validate: validateDueDate || createModalDueDateValidator() }),
    [validateDueDate]
  );

  useEffect(() => {
    if (open && assignment) {
      reset({ dueDate: assignment.date || '' });
    }
  }, [open, assignment, reset]);

  const onValidSubmit = (values) => {
    if (!assignment) {
      return;
    }

    const nextDate = returnStorageDate
      ? values.dueDate
      : convertDueDateToUtcIso(values.dueDate);

    onDone?.(assignment, nextDate);
  };

  const isDoneDisabled = !dueDateValue || Boolean(errors.dueDate);

  return (
    <AssignmentDueDateModalStylesRoot
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: classes.paper
      }}
    >
      <IconButton
        className={classes.closeButton}
        onClick={onClose}
        aria-label="Close"
      >
        <Icon name="CloseIcon" />
      </IconButton>

      <DialogContent className={classes.content}>
        <Typography component="h2" className={classes.title}>
          {title}
        </Typography>

        <div className={classes.scrollBody}>
          <div className={classes.body}>
            <AssignmentDueDateModalField
              name="dueDate"
              control={control}
              rules={dueDateRules}
              minDate={minDate}
              maxDate={maxDate}
              filterDate={filterDate}
              placeholderText={placeholderText}
            />
          </div>
        </div>

        <div className={classes.footer}>
          <button
            type="button"
            className={classes.doneButton}
            onClick={handleSubmit(onValidSubmit)}
            disabled={isDoneDisabled}
          >
            {doneLabel}
          </button>
        </div>
      </DialogContent>
    </AssignmentDueDateModalStylesRoot>
  );
};

AssignmentDueDateModal.propTypes = {
  open: PropTypes.bool,
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    date: PropTypes.string,
    dueDate: PropTypes.string,
    dateDue: PropTypes.string
  }),
  textInfo: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  filterDate: PropTypes.func,
  validateDueDate: PropTypes.func,
  returnStorageDate: PropTypes.bool,
  placeholderText: PropTypes.string
};

AssignmentDueDateModal.defaultProps = {
  open: false,
  assignment: null,
  textInfo: {},
  onClose: () => {},
  onDone: () => {},
  minDate: null,
  maxDate: null,
  filterDate: null,
  validateDueDate: null,
  returnStorageDate: false,
  placeholderText: 'mm/dd/yyyy'
};

export default AssignmentDueDateModal;
